/**
 * 字段规则类型：每个 value 描述一种表单字段的展示形态 + 表单 schema 类型。
 *
 * 不用 class 实现的原因：
 *  - class + static readonly + private constructor + values() + fromValue() 是 ~70 行模板代码；
 *  - 这里 27 条全是静态数据，没有实例行为（无方法），用 plain object + Record 更直接。
 *
 * 命名约定：
 *  - `value` 是后端 / 数据库存的业务编码（`NUMERIC` / `INTEGER` / ...），前端 UI 不要读它
 *  - `text` 是给用户看的中文文案
 *  - `color` 是 Arco Tag 颜色
 *  - `amis` 是 amis 渲染器名（仅供 amis 模板使用，本项目主要用 Arco）
 *  - `schemaType` 决定 JSON Schema 校验：`integer` / `number` / `string` / `boolean` / `array`
 */

// 注：项目用 TS 4.5，`as const satisfies Record<string, RuleType>`（TS 4.9+）不可用。
// 这里用「先 as const 再断言整体类型」两步替代：字面量类型仍由 as const 保留，
// 整体形状由显式 cast 到 `Record<keyof typeof SOURCE, RuleType>` 保证每个键都有 RuleType shape。
const _RULE_TYPES_SOURCE = {
  NUMERIC: {
    text: '数值',
    value: 'numeric',
    color: 'blue',
    amis: 'input-number',
    schemaType: 'integer'
  },
  FLOAT: {
    text: '小数',
    value: 'float',
    color: 'green',
    amis: 'input-number',
    schemaType: 'number'
  },
  INT: {
    text: '小数',
    value: 'int',
    color: 'green',
    amis: 'input-number',
    schemaType: 'number'
  },
  INTEGER: {
    text: '整数',
    value: 'integer',
    color: 'green',
    amis: 'input-number',
    schemaType: 'integer'
  },
  STRING: {
    text: '文本',
    value: 'string',
    color: 'cyan',
    amis: 'input-text',
    schemaType: 'string'
  },
  DATE: {
    text: '日期',
    value: 'date',
    color: 'volcano',
    amis: 'input-date',
    schemaType: 'string'
  },
  DATETIME: {
    text: '日期',
    value: 'datetime',
    color: 'volcano',
    amis: 'input-date',
    schemaType: 'string'
  },
  TIME: {
    text: '时间',
    value: 'time',
    color: 'orange',
    amis: 'input-datetime',
    schemaType: 'string'
  },
  BOOLEAN: {
    text: '布尔',
    value: 'boolean',
    color: 'gold',
    amis: 'switch',
    schemaType: 'boolean'
  },
  ENUM: {
    text: '枚举',
    value: 'enum',
    color: 'lime',
    amis: 'select',
    schemaType: 'string'
  },
  IMAGE: {
    text: '图片',
    value: 'image',
    color: 'green',
    amis: 'input-image',
    schemaType: 'string'
  },
  PHONE: {
    text: '手机',
    value: 'phone',
    color: 'cyan',
    amis: 'input-text',
    schemaType: 'string'
  },
  FILE: {
    text: '附件',
    value: 'file',
    color: 'cyan',
    amis: 'input-file',
    schemaType: 'string'
  },
  LIST: {
    text: '列表',
    value: 'list',
    color: 'blue',
    amis: 'select',
    schemaType: 'array'
  },
  NAME: {
    text: '人名',
    value: 'name',
    color: 'lime',
    amis: 'input-text',
    schemaType: 'string'
  },
  TEXT: {
    text: '长文本',
    value: 'text',
    color: 'green',
    amis: 'textarea',
    schemaType: 'string'
  },
  RICH: {
    text: '富文本',
    value: 'rich',
    color: 'cyan',
    amis: 'input-rich-text',
    schemaType: 'string'
  },
  RADIO: {
    text: '单选',
    value: 'radio',
    color: 'blue',
    amis: 'radio',
    schemaType: 'string'
  },
  CHECKBOX: {
    text: '多选',
    value: 'checkbox',
    color: 'gold',
    amis: 'checkbox',
    schemaType: 'string'
  },
  SIGNATURE: {
    text: '签章',
    value: 'signature',
    color: 'orange',
    amis: 'input-text',
    schemaType: 'string'
  },
  GENERATE: {
    text: '自动生成',
    value: 'generate',
    color: 'blue',
    amis: 'input-text',
    schemaType: 'string'
  },
  NI: {
    text: '人名图片',
    value: 'name_img',
    color: 'lime',
    amis: 'input-text',
    schemaType: 'string'
  },
  NAME_SIGN: {
    text: '人名和签名',
    value: 'name_sign',
    color: 'lime',
    amis: 'input-text',
    schemaType: 'string'
  },
  AGENCY: {
    text: '机构名',
    value: 'agency',
    color: 'blue',
    amis: 'input-text',
    schemaType: 'string'
  },
  SIGN: {
    text: '机构签章',
    value: 'sign',
    color: 'blue',
    amis: 'input-text',
    schemaType: 'string'
  },
  AGENCY_SIGN: {
    text: '机构名和签章',
    value: 'agency_sign',
    color: 'blue',
    amis: 'input-text',
    schemaType: 'string'
  },
  PLACE: {
    text: '地址',
    value: 'place',
    color: 'blue',
    amis: 'input-text',
    schemaType: 'string'
  },
  COUNTERSIGN: {
    text: '会签',
    value: 'countersign',
    color: 'blue',
    amis: 'select',
    schemaType: 'string'
  }
};
export const RULE_TYPES = _RULE_TYPES_SOURCE;

/** RuleType.value 联合类型（所有合法的业务编码字面量） */

/** 按声明顺序输出全部规则（替代 class 的 `values()`） */
export const RULE_TYPE_VALUES = Object.values(RULE_TYPES);

/** 按业务编码查规则（替代 class 的 `fromValue()`） */
export function findRuleType(value) {
  return RULE_TYPES[value];
}

/** 类型守卫：判断字符串是否是合法的 RuleType.value */
export function isRuleTypeValue(value) {
  return value in RULE_TYPES;
}